/**
 * What today is, and whether it is worth stopping the app for.
 *
 * Birthdays get one moment per person per day, marked locally the same way
 * the daily check-in marks itself. Missing the moment because a tab was
 * refreshed is fine; showing it twice is not.
 */

export type OccasionKind = 'self' | 'child' | 'family';

export interface Occasion {
  kind: OccasionKind;
  name: string;
  relationship: string | null;
  /** Whole years, when the year of birth was given. */
  turning: number | null;
}

const KEY = (userId: number | string, date: string) => `mmai.occasion.v1.${userId}.${date}`;

export function localDateISO(d: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function readOccasionMark(userId: number | string | undefined, date: string): boolean {
  if (userId === undefined) return false;
  try {
    return localStorage.getItem(KEY(userId, date)) === 'seen';
  } catch {
    return false;
  }
}

export function writeOccasionMark(userId: number | string | undefined, date: string): void {
  if (userId === undefined) return;
  try {
    localStorage.setItem(KEY(userId, date), 'seen');
  } catch {
    // A private window just means the moment can come round again.
  }
}

/**
 * The line that opens the moment.
 *
 * Their own birthday is addressed to them; everyone else's is addressed
 * about them, because "Happy birthday" to a parent of a three-year-old is
 * the wrong sentence.
 */
export function occasionGreeting(o: Occasion, firstName: string): string {
  if (o.kind === 'self') return `Happy birthday, ${firstName || o.name}.`;
  return `It's ${o.name}'s birthday.`;
}

export function occasionLine(o: Occasion): string {
  if (o.kind === 'self') {
    return o.turning
      ? `${o.turning} today. We hope somebody makes a fuss of you.`
      : 'We hope somebody makes a fuss of you.';
  }
  const who = o.kind === 'child'
    ? 'your'
    : `your ${String(o.relationship || 'family').toLowerCase()},`;
  const age = o.turning ? ` They're ${o.turning} today.` : '';
  return o.kind === 'child'
    ? `Today is ${who} child ${o.name}'s day.${age}`
    : `${o.name} is ${who} and today is their day.${age}`;
}

/** More than one person can share a date; the rest are mentioned, not staged. */
export function alsoToday(occasions: Occasion[]): string | null {
  const rest = occasions.slice(1);
  if (!rest.length) return null;
  const names = rest.map((o) => o.name);
  if (names.length === 1) return `It's also ${names[0]}'s birthday today.`;
  return `Also today: ${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}.`;
}
